import { User } from '../context/UserContext';
import { supabase } from '../lib/supabaseClient';
import type { Session } from '@supabase/supabase-js';
import { apiConfig } from './apiConfig';

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterCredentials {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
  inviteToken: string;
}

export interface AuthResponse {
  success: boolean;
  user?: User;
  session?: Session | null;
  error?: string;
  message?: string;
  requiresEmailVerification?: boolean;
  email?: string;
}

class AuthService {
  private userKey = 'user_data';
  private readonly authTimeoutMs = 6000;

  private async withTimeout(promise: Promise<any>, timeoutMs = this.authTimeoutMs): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error('AUTH_REQUEST_TIMEOUT')), timeoutMs);
      promise
        .then((value) => {
          clearTimeout(timer);
          resolve(value);
        })
        .catch((error) => {
          clearTimeout(timer);
          reject(error);
        });
    });
  }

  /**
   * Register new user with Supabase Auth
   */
  async register(credentials: RegisterCredentials): Promise<AuthResponse> {
    try {
      // Validate passwords match
      if (credentials.password !== credentials.confirmPassword) {
        return { success: false, error: 'Passwords do not match' };
      }

      if (!credentials.inviteToken?.trim()) {
        return { success: false, error: 'Invite token is required.' };
      }

      const baseURL = apiConfig.getBaseURL();
      const responseRaw = await this.withTimeout(fetch(`${baseURL}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          name: credentials.name,
          email: credentials.email,
          password: credentials.password,
          confirmPassword: credentials.confirmPassword,
          inviteToken: credentials.inviteToken
        })
      }));

      const payload = await responseRaw.json().catch(() => null) as {
        success?: boolean;
        error?: string;
        code?: string;
        message?: string;
        requiresEmailVerification?: boolean;
        email?: string;
        user?: User;
        tokens?: {
          access_token?: string;
          refresh_token?: string;
        };
        session?: Session | null;
      } | null;

      if (!responseRaw.ok || !payload?.success) {
        return {
          success: false,
          error: payload?.error || 'Registration failed'
        };
      }
      if (payload.requiresEmailVerification) {
        return {
          success: true,
          requiresEmailVerification: true,
          email: payload.email || credentials.email,
          message: payload.message || 'Please verify your email before signing in.'
        };
      }

      const formattedUser = payload.user || await this.hydrateCurrentUserFromApi();
      if (!formattedUser) {
        return { success: false, error: 'Registration succeeded, but user profile could not be loaded.' };
      }

      localStorage.setItem(this.userKey, JSON.stringify(formattedUser));

      return {
        success: true,
        user: formattedUser,
        session: payload.session || null
      };

    } catch (error) {
      if (error instanceof Error && error.message === 'AUTH_REQUEST_TIMEOUT') {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Registration request timed out.');
        }
        return { success: false, error: 'Sign up timed out. Please check your connection and try again.' };
      }
      console.error('Registration error:', error);
      const errorMessage = error instanceof Error ? error.message : String(error);
      return { success: false, error: errorMessage };
    }
  }

  /**
   * Login user with Supabase Auth
   */
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    try {
      // Sign in with Supabase Auth
      const { data, error } = await this.withTimeout(supabase.auth.signInWithPassword({
        email: credentials.email,
        password: credentials.password
      }));

      if (error) {
        console.error('Login error:', error);
        
        // Map Supabase errors to user-friendly messages
        if (error.message.includes('Invalid login credentials')) {
          return { success: false, error: 'Invalid email or password. Please try again.' };
        }
        if (/email not confirmed|email not verified/i.test(error.message)) {
          return { success: false, error: 'Please verify your email before signing in.' };
        }
        
        return { success: false, error: error.message };
      }

      if (!data.user) {
        return { success: false, error: 'Login failed' };
      }

      const formattedUser = await this.hydrateCurrentUserFromApi();
      const fallbackUser = this.formatUser(data.user);

      // Store user data in localStorage
      localStorage.setItem(this.userKey, JSON.stringify(formattedUser || fallbackUser));

      return {
        success: true,
        user: formattedUser || fallbackUser,
        session: data.session
      };

    } catch (error) {
      if (error instanceof Error && error.message === 'AUTH_REQUEST_TIMEOUT') {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Login request timed out.');
        }
        return { success: false, error: 'Sign in timed out. Please check your connection and try again.' };
      }
      console.error('Login error:', error);
      const errorMessage = error instanceof Error ? error.message : String(error);
      return { success: false, error: errorMessage };
    }
  }

  /**
   * Start Google OAuth login flow
   */
  async loginWithGoogle(): Promise<AuthResponse> {
    try {
      const redirectTo = `${window.location.origin}/auth/callback?mode=oauth`;
      const { error } = await this.withTimeout(supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo,
          queryParams: {
            prompt: 'select_account'
          }
        }
      }));

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Google sign-in failed';
      return { success: false, error: errorMessage };
    }
  }

  /**
   * Logout user with Supabase Auth
   */
  async logout(): Promise<void> {
    try {
      // Sign out with Supabase Auth
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        console.warn('Logout error:', error);
      }
    } catch (error) {
      console.warn('Logout error:', error);
    } finally {
      // Always clear local storage
      localStorage.removeItem(this.userKey);
    }
  }

  /**
   * Get current user from Supabase session
   */
  async getCurrentUser(): Promise<User | null> {
    try {
      // Require a valid Supabase session before trusting cached user data.
      const { data: { session } } = await this.withTimeout(supabase.auth.getSession());
      if (!session) {
        localStorage.removeItem(this.userKey);
        return null;
      }

      const hydratedUser = await this.hydrateCurrentUserFromApi();
      if (hydratedUser) {
        localStorage.setItem(this.userKey, JSON.stringify(hydratedUser));
        return hydratedUser;
      }

      // Try to get from localStorage first (faster fallback)
      const cachedUser = localStorage.getItem(this.userKey);
      if (cachedUser) {
        try {
          const parsed = JSON.parse(cachedUser) as User;
          // Never trust cached tester privileges without server hydration.
          return { ...parsed, isTester: false, isAdmin: false };
        } catch (e) {
          console.error('Error parsing cached user:', e);
        }
      }

      // Fallback to Supabase
      const { data: { user } } = await this.withTimeout(supabase.auth.getUser());
      
      if (user) {
        const formattedUser = this.formatUser(user);
        localStorage.setItem(this.userKey, JSON.stringify(formattedUser));
        return formattedUser;
      }

      return null;
    } catch (error) {
      if (error instanceof Error && error.message === 'GOOGLE_INVITE_REQUIRED') {
        throw new Error('GOOGLE_INVITE_REQUIRED');
      }
      if (error instanceof Error && error.message === 'AUTH_REQUEST_TIMEOUT') {
        return null;
      }
      console.error('Error getting current user:', error);
      return null;
    }
  }

  /**
   * Get current session
   */
  async getSession(): Promise<Session | null> {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      return session;
    } catch (error) {
      console.error('Error getting session:', error);
      return null;
    }
  }

  /**
   * Refresh session (Supabase handles this automatically)
   */
  async refreshToken(): Promise<AuthResponse> {
    try {
      const { data, error } = await supabase.auth.refreshSession();

      if (error || !data.session) {
        this.logout();
        return { success: false, error: 'Session expired' };
      }

      return { success: true, session: data.session };
    } catch (error) {
      console.error('Token refresh error:', error);
      this.logout();
      return { success: false, error: 'Failed to refresh token' };
    }
  }

  /**
   * Check if user is authenticated
   */
  async isAuthenticated(): Promise<boolean> {
    try {
      const session = await this.getSession();
      return !!session;
    } catch (error) {
      return false;
    }
  }

  /**
   * Refresh user data from server
   */
  async refreshUser(): Promise<AuthResponse> {
    try {
      const { data: { session }, error } = await supabase.auth.getSession();

      if (error || !session) {
        this.logout();
        return { success: false, error: 'Session expired' };
      }

      const hydratedUser = await this.hydrateCurrentUserFromApi();
      if (hydratedUser) {
        localStorage.setItem(this.userKey, JSON.stringify(hydratedUser));
        return { success: true, user: hydratedUser };
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        return { success: false, error: 'Session expired' };
      }

      const fallbackUser = this.formatUser(user);
      localStorage.setItem(this.userKey, JSON.stringify(fallbackUser));

      return { success: true, user: fallbackUser };
    } catch (error) {
      console.error('Refresh user error:', error);
      return { 
        success: false, 
        error: 'Failed to refresh user data' 
      };
    }
  }

  private async hydrateCurrentUserFromApi(): Promise<User | null> {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return null;

      const baseURL = apiConfig.getBaseURL();
      const responseRaw = await this.withTimeout(fetch(`${baseURL}/auth/me`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        credentials: 'include',
      }), 5000);

      if (!responseRaw.ok) {
        const errorPayload = await responseRaw.json().catch(() => null) as { code?: string } | null;
        if (errorPayload?.code === 'GOOGLE_INVITE_REQUIRED') {
          throw new Error('GOOGLE_INVITE_REQUIRED');
        }
        return null;
      }

      const response = await responseRaw.json() as { success: boolean; user?: User };
      if (response?.success && response.user) {
        return response.user;
      }
      return null;
    } catch (error) {
      if (error instanceof Error && error.message === 'GOOGLE_INVITE_REQUIRED') {
        throw error;
      }
      return null;
    }
  }

  async activateGoogleInvite(inviteToken: string): Promise<AuthResponse> {
    try {
      const normalizedToken = inviteToken.trim();
      if (!normalizedToken) {
        return { success: false, error: 'Invite token is required.' };
      }

      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        return { success: false, error: 'Sign-in session is missing. Please sign in with Google again.' };
      }

      const baseURL = apiConfig.getBaseURL();
      const responseRaw = await this.withTimeout(fetch(`${baseURL}/auth/activate-google-invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`
        },
        credentials: 'include',
        body: JSON.stringify({ inviteToken: normalizedToken })
      }));

      const payload = await responseRaw.json().catch(() => null) as {
        success?: boolean;
        error?: string;
        message?: string;
        code?: string;
      } | null;

      if (!responseRaw.ok || !payload?.success) {
        return { success: false, error: payload?.error || 'Failed to activate invite token.' };
      }

      return {
        success: true,
        message: payload.message || 'Invite activated successfully.'
      };
    } catch (error) {
      console.error('Activate Google invite error:', error);
      return {
        success: false,
        error: 'Failed to activate invite token.'
      };
    }
  }

  /**
   * Update user profile
   */
  async updateProfile(updates: Partial<User>): Promise<AuthResponse> {
    try {
      const { data, error } = await supabase.auth.updateUser({
        data: {
          name: updates.name,
          avatar_url: updates.avatar
        }
      });

      if (error) {
        return { success: false, error: error.message };
      }

      if (data.user) {
        const formattedUser = this.formatUser(data.user);
        localStorage.setItem(this.userKey, JSON.stringify(formattedUser));
        return { success: true, user: formattedUser };
      }

      return { success: false, error: 'Profile update failed' };
    } catch (error) {
      console.error('Update profile error:', error);
      return { 
        success: false, 
        error: 'Failed to update profile' 
      };
    }
  }

  /**
   * Change password
   */
  async changePassword(currentPassword: string, newPassword: string): Promise<AuthResponse> {
    try {
      // Supabase requires re-authentication before password change
      const user = await this.getCurrentUser();
      if (!user || !user.email) {
        return { success: false, error: 'User not authenticated' };
      }

      // Re-authenticate
      const { error: reAuthError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: currentPassword
      });

      if (reAuthError) {
        return { success: false, error: 'Current password is incorrect' };
      }

      // Update password
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      console.error('Change password error:', error);
      return { 
        success: false, 
        error: 'Failed to change password' 
      };
    }
  }

  /**
   * Request password reset
   */
  async requestPasswordReset(email: string): Promise<AuthResponse> {
    try {
      const baseURL = apiConfig.getBaseURL();
      const responseRaw = await this.withTimeout(fetch(`${baseURL}/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ email })
      }));

      const payload = await responseRaw.json().catch(() => null) as {
        success?: boolean;
        error?: string;
        message?: string;
      } | null;

      if (!responseRaw.ok || !payload?.success) {
        return { success: false, error: payload?.error || 'Failed to request password reset' };
      }

      return { success: true, message: payload.message || 'If an account exists, a reset link has been sent.' };
    } catch (error) {
      console.error('Password reset request error:', error);
      return {
        success: false,
        error: 'Failed to request password reset'
      };
    }
  }

  async resendVerification(email: string): Promise<AuthResponse> {
    try {
      const baseURL = apiConfig.getBaseURL();
      const responseRaw = await this.withTimeout(fetch(`${baseURL}/auth/resend-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ email })
      }));

      const payload = await responseRaw.json().catch(() => null) as {
        success?: boolean;
        error?: string;
        message?: string;
      } | null;

      if (!responseRaw.ok || !payload?.success) {
        return { success: false, error: payload?.error || 'Failed to resend verification email' };
      }

      return { success: true, message: payload.message || 'Verification email sent.' };
    } catch (error) {
      console.error('Resend verification error:', error);
      return {
        success: false,
        error: 'Failed to resend verification email'
      };
    }
  }

  async completeVerification(): Promise<AuthResponse> {
    try {
      let session = (await supabase.auth.getSession()).data.session;

      // Supabase may take a moment to hydrate session from callback hash/code.
      if (!session?.access_token) {
        for (let attempt = 0; attempt < 10; attempt++) {
          await new Promise((resolve) => setTimeout(resolve, 300));
          session = (await supabase.auth.getSession()).data.session;
          if (session?.access_token) break;
        }
      }

      if (!session?.access_token) {
        return { success: false, error: 'Verification session is missing. Please open the email link again.' };
      }

      const baseURL = apiConfig.getBaseURL();
      const responseRaw = await this.withTimeout(fetch(`${baseURL}/auth/complete-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`
        },
        credentials: 'include'
      }));

      const payload = await responseRaw.json().catch(() => null) as {
        success?: boolean;
        error?: string;
        message?: string;
        code?: string;
      } | null;

      if (!responseRaw.ok || !payload?.success) {
        return { success: false, error: payload?.error || 'Failed to complete verification' };
      }

      return { success: true, message: payload.message || 'Email verification completed.' };
    } catch (error) {
      console.error('Complete verification error:', error);
      return {
        success: false,
        error: 'Failed to complete verification'
      };
    }
  }

  /**
   * Reset password with token (from email link)
   */
  async resetPassword(newPassword: string): Promise<AuthResponse> {
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      console.error('Password reset error:', error);
      return { 
        success: false, 
        error: 'Failed to reset password' 
      };
    }
  }

  /**
   * Format Supabase user to our User type
   */
  private formatUser(supabaseUser: any): User {
    return {
      id: supabaseUser.id,
      name: supabaseUser.user_metadata?.name || supabaseUser.email?.split('@')[0] || 'User',
      email: supabaseUser.email || '',
      avatar: supabaseUser.user_metadata?.avatar_url || undefined,
      isPremium: supabaseUser.user_metadata?.is_premium || false,
      // Source of truth is backend /auth/me (users table flags), not Supabase metadata.
      isTester: false,
      isAdmin: false,
      joinDate: supabaseUser.created_at,
      stats: {
        totalEssays: 0,
        averageScore: 0,
        currentStreak: 0,
        weeklyGoal: 5,
        completedGoals: 0
      }
    };
  }

  /**
   * Get user ID (for backward compatibility)
   */
  getUserId(): string | null {
    const user = localStorage.getItem(this.userKey);
    if (user) {
      try {
        const parsed = JSON.parse(user);
        return parsed.id;
      } catch (e) {
        return null;
      }
    }
    return null;
  }
}

export const authService = new AuthService();
